import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SiteFooter } from "@/components/SiteFooter";
import { Check, ArrowRight, ChevronRight } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};
const panel = "rounded-2xl border border-white/5 bg-gradient-to-b from-[hsl(222,42%,15%)] to-[hsl(222,46%,10%)] shadow-xl shadow-black/30 ring-1 ring-white/5";

const TITLE = "Pricing — $9.99/mo, one flat plan | Send-Proof";
const DESC = "Send-Proof pricing: $9.99 a month or $99 a year for unlimited jobs, photos, open tracking, and shareable proof links. 15-day free trial, no credit card.";

const includes = [
  "Unlimited jobs & reports",
  "Unlimited photos & proof",
  "Real-time open tracking",
  "Shareable proof links + QR",
  "Sort & send by trade",
  "PDF exports",
  "Everyone you send lists to works free — no account, no charge",
];

const faqs = [
  { q: "What happens after my trial?", a: "Your 15-day trial is free with no credit card. After that it's $9.99/month for everything — cancel anytime, no lock-in." },
  { q: "Do the people I send lists to cost extra?", a: "No. It's one flat $9.99/month for you. The crew and clients you send lists to never pay or create an account — they just open the link and add their photos." },
  { q: "Can I cancel anytime?", a: "Yes. One flat plan, month to month. Cancel in a couple of taps — no contract." },
  { q: "Do the people I send lists to need to pay or sign up?", a: "No. They open your link, mark items and add photos. Nothing to install, no account, no charge." },
];

export default function Pricing() {
  const navigate = useNavigate();
  const go = () => navigate("/auth?mode=signup");
  const [period, setPeriod] = useState("monthly");

  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : null;
    if (desc) desc.setAttribute("content", DESC);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "ld-pricing";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "Product", name: "Send-Proof", description: DESC, offers: { "@type": "Offer", price: "9.99", priceCurrency: "USD" } },
        { "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
      ],
    });
    document.head.appendChild(ld);
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
      const el = document.getElementById("ld-pricing");
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-card text-foreground">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[800px]">
        <div className="absolute left-1/2 top-[-200px] h-[500px] w-[820px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[130px]" />
      </div>
      <div className="relative z-10">
        <header className="border-b border-white/10 bg-[hsl(222,46%,10%)]">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-logo">
            <img src="/logo-mark.webp" alt="Send-Proof" className="h-8 w-8 rounded-lg" />
            <span className="font-heading text-lg font-bold tracking-tight">Send-Proof</span>
          </Link>
          <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="pricing-nav-cta" onClick={go}>Start free trial</Button>
          </div>
        </header>

        <motion.section variants={reveal} initial="hidden" animate="show" className="mx-auto max-w-2xl px-5 pt-10 text-center sm:px-8 sm:pt-16" data-testid="pricing-page">
          <h1 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">One plan. One flat price.</h1>
          <p className="mx-auto mt-4 max-w-lg text-base text-muted-foreground sm:text-lg">
            Send-Proof is a punch list app you pay one flat price for. No tiers, no upsells — less than a single site visit, every month.
          </p>
        </motion.section>

        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mx-auto max-w-md px-5 pt-10 sm:px-8">
          <div className="mx-auto mb-6 flex w-fit items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1" data-testid="pricing-period-toggle">
            <button
              type="button"
              data-testid="pricing-period-monthly"
              onClick={() => setPeriod("monthly")}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${period === "monthly" ? "bg-emerald-500 text-white" : "text-muted-foreground hover:text-foreground"}`}
            >
              Monthly
            </button>
            <button
              type="button"
              data-testid="pricing-period-annual"
              onClick={() => setPeriod("annual")}
              className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${period === "annual" ? "bg-emerald-500 text-white" : "text-muted-foreground hover:text-foreground"}`}
            >
              Annual <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${period === "annual" ? "bg-white/20 text-white" : "bg-emerald-500/15 text-emerald-400"}`}>2 months free</span>
            </button>
          </div>
          <div className="relative rounded-2xl border-2 border-emerald-500 bg-gradient-to-b from-[hsl(222,44%,14%)] to-[hsl(222,47%,9%)] p-7 shadow-2xl shadow-emerald-500/20" data-testid="pricing-card">
            <div className="text-xs font-semibold uppercase tracking-wider text-emerald-500">Everything, one plan</div>
            <p className="mt-2 text-sm font-semibold text-emerald-400">Less than a single site visit.</p>
            <div className="mt-1 flex items-end gap-1">
              <span className="font-heading text-5xl font-extrabold">{period === "annual" ? "$99" : "$9.99"}</span>
              <span className="pb-2 text-muted-foreground">{period === "annual" ? "/year" : "/month"}</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {period === "annual"
                ? "Pay yearly and get two months free — that's $8.25/month. The people you send lists to always work free."
                : "One flat price for you — $9.99/month. Everyone you send lists to opens them free, no account needed."}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">15-day free trial. <span className="font-semibold text-foreground">No credit card required.</span> Cancel anytime.</p>
            <ul className="mt-5 space-y-2 text-sm text-foreground">
              {includes.map((f) => (
                <li key={f} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {f}</li>
              ))}
            </ul>
            <Button className="mt-6 h-12 w-full bg-emerald-500 text-base hover:bg-emerald-600" data-testid="pricing-cta" onClick={go}>Start 15-day free trial</Button>
          </div>
        </motion.section>

        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-2xl px-5 pb-24 pt-24 sm:px-8" data-testid="pricing-faq">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Pricing questions</h2>
          <div className="mt-8 space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className={`${panel} group p-5`} data-testid="pricing-faq-item">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-heading text-base font-bold">
                  {f.q}
                  <ChevronRight className="h-5 w-5 shrink-0 text-emerald-500 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button size="lg" className="h-12 bg-emerald-500 px-8 text-base hover:bg-emerald-600" data-testid="pricing-bottom-cta" onClick={go}>
              Start 15-day free trial <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </motion.section>

        <SiteFooter />
      </div>
    </div>
  );
}
